/**
 * Visit + Encounter creation service.
 *
 * Core rule: always ensure a Visit exists before creating an Encounter.
 * Reuse an open visit only if visit_date matches the target date.
 */

export interface EnsureVisitParams {
  // PowerSyncDatabase — accepts any object with execute + getAll
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  orgId: string;
  clinicId?: string | null;
  patientId: string;
  queueId?: string | null;
  appointmentId?: string | null;
  providerId?: string | null;
  encounterType?: string; // default 'consultation'
}

export interface EnsureVisitResult {
  visitId: string;
  encounterId: string;
  visitCreated: boolean;
}

/**
 * Get today's date as YYYY-MM-DD in local timezone.
 */
function todayLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Ensure a visit exists for the patient today, then create an encounter linked to it.
 *
 * - Queries for an open visit matching today's date
 * - If none, creates a new visit
 * - Creates an encounter linked to the visit
 * - Returns { visitId, encounterId, visitCreated }
 */
export async function ensureVisitThenCreateEncounter(
  params: EnsureVisitParams,
): Promise<EnsureVisitResult> {
  const {
    db,
    orgId,
    clinicId,
    patientId,
    queueId,
    appointmentId,
    providerId,
    encounterType = 'consultation',
  } = params;

  const visitDate = todayLocal();
  const now = new Date().toISOString();

  // 1. Check for existing open visit today
  const existing = (await db.getAll(
    `SELECT id FROM patient_visits
     WHERE patient_id = ? AND visit_date = ? AND status = 'open'
     ORDER BY started_at DESC
     LIMIT 1`,
    [patientId, visitDate],
  )) as Array<{ id: string }>;

  let visitId: string;
  let visitCreated = false;

  if (existing.length > 0) {
    visitId = existing[0].id;
  } else {
    // 2. Create new visit
    visitId = crypto.randomUUID();
    await db.execute(
      `INSERT INTO patient_visits
        (id, org_id, clinic_id, patient_id, visit_date, status,
         queue_id, appointment_id, started_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        visitId,
        orgId,
        clinicId ?? null,
        patientId,
        visitDate,
        'open',
        queueId ?? null,
        appointmentId ?? null,
        now,
        now,
        now,
      ],
    );
    visitCreated = true;
  }

  // 3. Create encounter linked to the visit
  const encounterId = crypto.randomUUID();
  await db.execute(
    `INSERT INTO patient_encounters
      (id, org_id, clinic_id, patient_id, visit_id,
       queue_id, appointment_id, provider_id,
       type, status, started_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      encounterId,
      orgId,
      clinicId ?? null,
      patientId,
      visitId,
      queueId ?? null,
      appointmentId ?? null,
      providerId ?? null,
      encounterType,
      'open',
      now,
      now,
      now,
    ],
  );

  return { visitId, encounterId, visitCreated };
}

/**
 * Resolve the target encounter for a Treatment Record-mode quick entry on a
 * given date (PRD.182). Rule:
 *   - If a Visit Note exists for the date → use the latest encounter for that
 *     date's latest visit.
 *   - If multiple Visit Notes exist on the same date → use the latest one.
 *   - If none exists → create a visit + encounter for that date in the
 *     background, then return its encounterId.
 *
 * The date is treated as a local YYYY-MM-DD string.
 */
export async function ensureEncounterForDate(params: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  orgId: string;
  clinicId?: string | null;
  patientId: string;
  /** Local YYYY-MM-DD. */
  date: string;
  encounterType?: string;
  // Optional queue/appointment/provider context, applied only when a new
  // visit/encounter is created (reuse leaves the existing row's links intact).
  queueId?: string | null;
  appointmentId?: string | null;
  providerId?: string | null;
}): Promise<{ visitId: string; encounterId: string; createdVisit: boolean }> {
  const {
    db,
    orgId,
    clinicId,
    patientId,
    date,
    encounterType = 'consultation',
    queueId = null,
    appointmentId = null,
    providerId = null,
  } = params;
  const now = new Date().toISOString();

  // 1. Find the latest visit for this patient on the target date (open or
  //    closed — we still want to attach to it). Multiple visits on the same
  //    date resolve to the most recent.
  const visits = (await db.getAll(
    `SELECT id FROM patient_visits
     WHERE patient_id = ? AND visit_date = ?
     ORDER BY started_at DESC, created_at DESC
     LIMIT 1`,
    [patientId, date],
  )) as Array<{ id: string }>;

  let visitId: string;
  let createdVisit = false;

  if (visits.length > 0) {
    visitId = visits[0].id;
  } else {
    // Create a new visit dated to the target day.
    visitId = crypto.randomUUID();
    await db.execute(
      `INSERT INTO patient_visits
        (id, org_id, clinic_id, patient_id, visit_date, status,
         queue_id, appointment_id, started_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        visitId,
        orgId,
        clinicId ?? null,
        patientId,
        date,
        'open',
        queueId,
        appointmentId,
        `${date}T00:00:00.000Z`,
        now,
        now,
      ],
    );
    createdVisit = true;
  }

  // 2. Find the latest encounter on this visit. PRD.182 §6.5 says use the
  //    latest visit note for that date.
  const encounters = (await db.getAll(
    `SELECT id FROM patient_encounters
     WHERE visit_id = ?
     ORDER BY started_at DESC, created_at DESC
     LIMIT 1`,
    [visitId],
  )) as Array<{ id: string }>;

  if (encounters.length > 0) {
    return { visitId, encounterId: encounters[0].id, createdVisit };
  }

  // 3. No encounter yet → create one in the background.
  const encounterId = crypto.randomUUID();
  await db.execute(
    `INSERT INTO patient_encounters
      (id, org_id, clinic_id, patient_id, visit_id,
       queue_id, appointment_id, provider_id,
       type, status, started_at, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      encounterId,
      orgId,
      clinicId ?? null,
      patientId,
      visitId,
      queueId,
      appointmentId,
      providerId,
      encounterType,
      'open',
      `${date}T00:00:00.000Z`,
      now,
      now,
    ],
  );

  return { visitId, encounterId, createdVisit };
}
